import {
  Box,
  Card,
  Checkbox,
  Input,
  Label,
  Radio,
  Select,
  Slider,
  Text,
  Textarea,
  Title,
  Toggle,
} from '@nimbus-ds/components';
import { ChevronDownIcon, ChevronUpIcon } from '@nimbus-ds/icons';
import { FormField } from '@nimbus-ds/patterns';
import { useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { commitOnBlur, commitWhileTyping } from './controls.model.js';

/** White card section with a clickable header that collapses its content. Open by default. */
export function CollapsibleSection({
  title,
  aside,
  defaultOpen = true,
  children,
}: {
  title: string;
  /**
   * Status shown in the header, beside the chevron — a `Tag`, a count, anything read-only.
   *
   * The point of a collapsed section is that the merchant can skip it, which only works if the
   * header says enough to decide. A tracking pixel whose last send FAILED used to show that only
   * once expanded, so a store could be losing every conversion while the list looked healthy.
   *
   * Keep it non-interactive: the whole header is one big button, so a control in here would need
   * the click stopped, and a header that sometimes toggles and sometimes does not is worse than
   * one more row inside the card.
   */
  aside?: ReactNode;
  defaultOpen?: boolean;
  children: ReactNode;
}): JSX.Element {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <Card>
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        gap="2"
        onClick={() => setOpen((o) => !o)}
        role="button"
        aria-expanded={open}
        // 🚨 The `cursor` PROP, not `style`. Nimbus `Box` does not forward `style` at all — this
        // header carried `style={{ cursor: 'pointer' }}` and measured `cursor: auto` in Chrome, so
        // the one affordance saying the row is clickable was silently absent. Every other
        // `cursor: 'pointer'` in this app is on a plain `div`/`button`, where `style` does work.
        cursor="pointer"
      >
        {/* 🚨 The title shrinks, the status does not. Both were free to shrink, so on a phone a
            long provider name ("Meta (Facebook / Instagram) pixel") wrapped to two lines and
            squeezed the `Tag` beside it until its own text broke mid-phrase — a status pill that
            has to be deciphered is worse than no pill. */}
        <Box minWidth="0">
          <Title as="h5">{title}</Title>
        </Box>
        <Box display="flex" alignItems="center" gap="2" flex="0 0 auto">
          {aside}
          {open ? <ChevronUpIcon /> : <ChevronDownIcon />}
        </Box>
      </Box>
      {open && (
        <Box marginTop="4" display="flex" flexDirection="column" gap="4">
          {children}
        </Box>
      )}
    </Card>
  );
}

/**
 * Labeled text input.
 *
 * 🚨 `error` is what makes a message VISIBLE. Nimbus's `FormField` gates its help text on
 * `showHelpText`, which defaults to false — so a `helpText` on its own is passed through and
 * never painted. An error the merchant cannot read is the same as no validation at all, so the
 * error branch turns the flag on and paints the field `danger`; the plain `helpText` case is left
 * exactly as every other caller already has it.
 */
export function TextControl({
  id,
  label,
  value,
  onChange,
  onBlur,
  helpText,
  error,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (v: string) => void;
  /** Commit point for inputs that hold a draft while focused — see `designer/options.ts`. */
  onBlur?: () => void;
  helpText?: string;
  /** Validation message. Replaces `helpText` and marks the field, so it cannot be missed. */
  error?: string;
}): JSX.Element {
  return (
    <FormField.Input
      id={id}
      label={label}
      value={value}
      helpText={error ?? helpText}
      {...(error ? { appearance: 'danger' as const, showHelpText: true } : {})}
      onChange={(e) => onChange((e.target as HTMLInputElement).value)}
      {...(onBlur ? { onBlur } : {})}
    />
  );
}

/** Labeled multi-line text input. Used for authoring raw HTML content and long messages. */
/**
 * A multi-line text control that sizes itself to its content.
 *
 * 🚨 **The drag handle is off, deliberately.** A `<textarea>` is resizable by default, and dragging
 * one inside the Designer's two-column layout pushes the whole panel around — a control for the
 * merchant to break their own editing surface with, in exchange for a height this component can
 * work out on its own.
 *
 * So it grows instead: between `lines` and `maxLines`, remeasured whenever the value changes, and
 * scrolling vertically past that rather than running away with the page. `maxLines` is what keeps
 * a 2000-character block or a wall of thank-you HTML from turning the panel into one long field.
 *
 * `height: 'auto'` before measuring is load-bearing — `scrollHeight` never reports less than the
 * element's current height, so measuring without the reset makes the box able to grow and never
 * shrink back when the merchant deletes a paragraph.
 */
export function TextAreaControl({
  id,
  label,
  value,
  onChange,
  helpText,
  error,
  lines = 4,
  maxLines = 14,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (v: string) => void;
  helpText?: string;
  /** Validation message. Replaces `helpText` and colours the copy, like `TextControl`. */
  error?: string;
  /** Height at rest, in lines — also the floor it never shrinks below. */
  lines?: number;
  /** Height it stops growing at; past this the box scrolls. */
  maxLines?: number;
}): JSX.Element {
  const ref = useRef<HTMLTextAreaElement>(null);

  // Layout effect, not a passive one: measuring after paint shows the merchant a frame at the old
  // height on every keystroke that adds a line.
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = 'auto';
    const box = getComputedStyle(el);
    const lineHeight = parseFloat(box.lineHeight) || 20;
    // Border-box sizing: `scrollHeight` excludes the border, so the frame has to be added back or
    // the box lands a couple of pixels short and scrolls by itself.
    const chrome =
      parseFloat(box.paddingTop) +
      parseFloat(box.paddingBottom) +
      parseFloat(box.borderTopWidth) +
      parseFloat(box.borderBottomWidth);
    const min = lines * lineHeight + chrome;
    const max = maxLines * lineHeight + chrome;
    const wanted = Math.max(el.scrollHeight, min);
    el.style.height = `${Math.min(wanted, max)}px`;
    el.style.overflowY = wanted > max ? 'auto' : 'hidden';
  }, [value, lines, maxLines]);

  return (
    <Box display="flex" flexDirection="column" gap="1">
      <Label htmlFor={id}>{label}</Label>
      <Textarea
        id={id}
        name={id}
        ref={ref}
        lines={lines}
        value={value}
        style={{ resize: 'none' }}
        onChange={(e) => onChange((e.target as HTMLTextAreaElement).value)}
      />
      {/* The error wins over the help text and is coloured `danger`: this control is hand-built
          from a `Textarea` rather than a `FormField`, so it has no `appearance` prop to inherit
          the marking `TextControl` gets for free. */}
      {(error ?? helpText) && (
        <Text fontSize="caption" color={error ? 'danger-textLow' : 'neutral-textLow'}>
          {error ?? helpText}
        </Text>
      )}
    </Box>
  );
}

/** Numeric input bounded to a min/max range. */
/**
 * A bounded number input the merchant can actually TYPE into.
 *
 * Draft-backed for the same reason the options list is (`designer/options.ts`): deriving the
 * input's text from the committed value destroys half-typed input. The rules live in
 * `controls.model.ts` — read the note there before changing either handler.
 *
 * Deliberately does NOT round: `shippingRate.price` is a plain `z.number()`, so `12.50` has to
 * survive. Callers whose schema wants an integer bound it there.
 */
export function NumberControl({
  id,
  label,
  value,
  onChange,
  min = 0,
  max = 100,
  error,
}: {
  id: string;
  label: string;
  value: number;
  onChange: (v: number) => void;
  min?: number;
  max?: number;
  /**
   * Validation message. Mostly unreachable while `min`/`max` here match the schema's bounds —
   * the commit helpers clamp to them — which is exactly why it is worth having: when they DON'T
   * match, the old symptom was a save refused with nothing marked (see the `submit-font` note in
   * `BlockControls`).
   */
  error?: string;
}): JSX.Element {
  /** The merchant's raw text while the input is focused; `null` when not editing. */
  const [draft, setDraft] = useState<string | null>(null);

  return (
    <FormField.Input
      id={id}
      label={label}
      type="number"
      value={draft ?? String(value)}
      {...(error ? { helpText: error, appearance: 'danger' as const, showHelpText: true } : {})}
      onChange={(e) => {
        const text = (e.target as HTMLInputElement).value;
        setDraft(text);
        const next = commitWhileTyping(text, min, max);
        if (next !== null) onChange(next);
      }}
      onBlur={() => {
        const next = commitOnBlur(draft, min, max);
        setDraft(null);
        if (next !== null) onChange(next);
      }}
    />
  );
}

/** Normalize a value for the native color input, which only accepts #rrggbb. */
function toNativeHex(value: string): string {
  const v = value.trim();
  if (/^#[0-9a-fA-F]{6}$/.test(v)) return v;
  if (/^#[0-9a-fA-F]{3}$/.test(v)) {
    return '#' + [...v.slice(1)].map((c) => c + c).join('');
  }
  // rgb()/rgba() — map the channels so the swatch reflects palette/preset colors too.
  const rgb = v.match(/^rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/i);
  if (rgb) {
    return (
      '#' +
      rgb
        .slice(1, 4)
        .map((n) => Math.min(255, Number(n)).toString(16).padStart(2, '0'))
        .join('')
    );
  }
  return '#000000'; // hsl/named/transparent have no native equivalent; swatch shows black
}

/**
 * The single color control used everywhere. A native hex swatch for quick picking plus a
 * text field that accepts ANY CSS color (hex, rgb/rgba, hsl, named, `transparent`), so the
 * UI is uniform but no format is blocked.
 */
export function ColorControl({
  id,
  label,
  value,
  onChange,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (v: string) => void;
}): JSX.Element {
  return (
    <Box display="flex" flexDirection="column" gap="1">
      <Label htmlFor={id}>{label}</Label>
      <Box display="flex" alignItems="center" gap="2">
        <input
          id={id}
          type="color"
          className="cod-color-swatch"
          value={toNativeHex(value)}
          onChange={(e) => onChange(e.target.value)}
        />
        {/* 🚨 `flex: 0 1 120px`, not `width: 120px`. Fixed, the swatch + gap + input made every
            colour control a hard 168px floor — two of them side by side needed 336px, which no
            phone card has, so the second one was simply sliced off (`rgba(211,47,47,1`, reported
            from a device). It still PREFERS 120px, so nothing changes on a desktop where there is
            room; `minWidth: 0` is what lets it give way when there is not. */}
        <Box flex="0 1 120px" minWidth="0">
          <Input
            id={`${id}-hex`}
            value={value}
            onChange={(e) => onChange((e.target as HTMLInputElement).value)}
          />
        </Box>
      </Box>
    </Box>
  );
}

/** Slider bound to a numeric value, with the current value shown on the right. */
export function SliderControl({
  label,
  value,
  onChange,
  min = 0,
  max = 40,
  step = 1,
  hideValue = false,
}: {
  label: string;
  value: number;
  onChange: (v: number) => void;
  min?: number;
  max?: number;
  step?: number;
  hideValue?: boolean;
}): JSX.Element {
  return (
    <Box display="flex" flexDirection="column" gap="1">
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Text>{label}</Text>
        {!hideValue && (
          <Text fontSize="caption" color="neutral-textLow">
            {value}
          </Text>
        )}
      </Box>
      <Slider value={value} min={min} max={max} step={step} onChange={(v) => onChange(v)} />
    </Box>
  );
}

/** Vertical radio group. */
export function RadioGroupControl<T extends string>({
  name,
  label,
  value,
  options,
  onChange,
}: {
  name: string;
  label: string;
  value: T;
  options: { value: T; label: string }[];
  onChange: (v: T) => void;
}): JSX.Element {
  return (
    <Box display="flex" flexDirection="column" gap="1">
      <Text>{label}</Text>
      <Box display="flex" flexDirection="column" gap="1">
        {options.map((o) => (
          // A unique id per radio is required: Nimbus derives the input id AND the label's
          // htmlFor from `id || name`, so without it every radio shares the group's name as its
          // id and every label targets the first one — making the other options unselectable.
          <Radio
            key={o.value}
            id={`${name}-${o.value}`}
            name={name}
            label={o.label}
            value={o.value}
            checked={value === o.value}
            onChange={() => onChange(o.value)}
          />
        ))}
      </Box>
    </Box>
  );
}

/** Labeled select bound to a string value. */
export function SelectControl<T extends string>({
  id,
  label,
  value,
  options,
  onChange,
}: {
  id: string;
  label: string;
  value: T;
  options: { value: T; label: string }[];
  onChange: (v: T) => void;
}): JSX.Element {
  return (
    <Box display="flex" flexDirection="column" gap="1">
      <Text>{label}</Text>
      <Select
        id={id}
        name={id}
        value={value}
        // No `cursor` here: Nimbus `Select` renders `style` on its wrapper rather than on the
        // `<select>`, so an inline one changes nothing. The affordance is a `select {}` rule in
        // `index.css` — see the note there.
        onChange={(e) => onChange((e.target as HTMLSelectElement).value as T)}
      >
        {options.map((o) => (
          <Select.Option key={o.value} value={o.value} label={o.label} />
        ))}
      </Select>
    </Box>
  );
}

/** Checkbox with a trailing label. */
export function CheckboxControl({
  name,
  label,
  checked,
  onChange,
}: {
  name: string;
  label: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}): JSX.Element {
  return (
    <Checkbox
      className="cod-checkbox"
      name={name}
      label={label}
      checked={checked}
      onChange={(e) => onChange((e.target as HTMLInputElement).checked)}
    />
  );
}

/**
 * Nimbus `Toggle`, made CONTROLLED. Use this instead of `Toggle` everywhere.
 *
 * 🚨 Nimbus maps its own `active` prop onto the input's **`defaultChecked`** (verified in
 * `@nimbus-ds/components` dist), which makes the switch uncontrolled: React applies the value when
 * the input mounts and ignores every later change to it. So a switch followed whatever the merchant
 * last clicked, no matter what the state behind it said — Discard on the quantity-offer list, the
 * navigation guard's "Discard changes", a failed save rolling back, all reverted the data and left
 * the switch showing the abandoned value.
 *
 * Passing `checked` reaches the same `<input>` through Nimbus's props spread and controls it.
 * `active` must be OMITTED rather than passed as well: React warns when an input carries both
 * `checked` and `defaultChecked`, and Nimbus emits `defaultChecked` whenever `active` is set.
 */
export function ControlledToggle({
  name,
  active,
  onChange,
  disabled,
}: {
  name: string;
  active: boolean;
  onChange: (v: boolean) => void;
  disabled?: boolean;
}): JSX.Element {
  return (
    <Toggle name={name} checked={active} disabled={disabled} onChange={() => onChange(!active)} />
  );
}

/** Inline toggle with a label on the left. */
export function ToggleControl({
  name,
  label,
  active,
  onChange,
}: {
  name: string;
  label: string;
  active: boolean;
  onChange: (v: boolean) => void;
}): JSX.Element {
  return (
    <Box display="flex" alignItems="center" justifyContent="space-between" gap="2">
      <Text>{label}</Text>
      <ControlledToggle name={name} active={active} onChange={onChange} />
    </Box>
  );
}
